import React, { Component } from 'react';
import { flowRight as compose } from 'lodash';
import { withQuery, withMutation } from '../../../hoc/withGraphql';
import getAllSeoLandingPages from './getAllSeoLandingPages.graphql';
import getSeoServices from './getSeoServices.graphql';
import setSeoLandingPagesStatus from './setSeoLandingPagesStatus.graphql';

const RED = '#D32F2F';
const PAGE_SIZE = 25;

class ManageSeoPages extends Component {
  constructor(props) {
    super(props);
    this.state = {
      serviceId: '', stateCode: '', status: '', search: '',
      currentPage: 1, selected: {}, busy: false, message: '',
    };
  }

  vars() {
    const { serviceId, stateCode, status, search, currentPage } = this.state;
    return {
      serviceId: serviceId ? Number(serviceId) : undefined,
      stateCode: stateCode || undefined,
      status: status || undefined,
      search: search || undefined,
      currentPage,
      limit: PAGE_SIZE,
    };
  }

  refetch = () => this.props.seoPages.refetch(this.vars());

  apply = () => this.setState({ currentPage: 1, selected: {} }, this.refetch);

  go = (delta) => this.setState((s) => ({ currentPage: Math.max(1, s.currentPage + delta), selected: {} }), this.refetch);

  toggle = (id) => this.setState((s) => ({ selected: { ...s.selected, [id]: !s.selected[id] } }));

  selectedIds = () => Object.keys(this.state.selected).filter((k) => this.state.selected[k]).map(Number);

  run = async (variables) => {
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.setStatus({ variables });
      const r = res && res.data && res.data.setSeoLandingPagesStatus;
      if (r && r.status === 200) {
        this.setState({ busy: false, selected: {}, message: `Updated ${r.updated} page(s).` }, this.refetch);
      } else {
        this.setState({ busy: false, message: (r && r.errorMessage) || 'Something went wrong.' });
      }
    } catch (e) {
      this.setState({ busy: false, message: e.message });
    }
  };

  bulk = (status) => {
    const ids = this.selectedIds();
    if (!ids.length) { this.setState({ message: 'Select at least one page first.' }); return; }
    this.run({ ids, status });
  };

  applyToFilter = (status) => {
    const { serviceId, stateCode } = this.state;
    if (!serviceId && !stateCode) { this.setState({ message: 'Choose a service and/or state to mass-apply.' }); return; }
    if (!window.confirm(`Set ALL pages matching the current service/state filter to "${status}"?`)) return;
    this.run({ serviceId: serviceId ? Number(serviceId) : undefined, stateCode: stateCode || undefined, status });
  };

  render() {
    const data = this.props.seoPages || {};
    const list = (data.getAllSeoLandingPages) || {};
    const rows = list.results || [];
    const services = (this.props.services && this.props.services.getSeoServices && this.props.services.getSeoServices.results) || [];
    const totalPages = Math.max(1, Math.ceil((list.count || 0) / PAGE_SIZE));

    const input = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginRight: 8 };
    const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', marginRight: 8 });
    const badge = (st) => ({ padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, color: '#fff', background: st === 'published' ? '#2E7D32' : '#9E9E9E' });

    return (
      <div style={{ fontFamily: 'Poppins, sans-serif' }}>
        <h1 className="siteAdminPrimaryHeading">SEO Pages</h1>

        {/* Counts */}
        <div style={{ display: 'flex', gap: 16, margin: '8px 0 18px' }}>
          {[['Total', list.totalCount], ['Published', list.publishedCount], ['Draft (ready to activate)', list.draftCount]].map(([l, v]) => (
            <div key={l} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: '12px 18px', minWidth: 140 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: RED }}>{v != null ? v : '—'}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{l}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <select style={input} value={this.state.serviceId} onChange={(e) => this.setState({ serviceId: e.target.value })}>
            <option value="">All services</option>
            {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input style={{ ...input, width: 90 }} placeholder="State (e.g. TX)" value={this.state.stateCode}
                 onChange={(e) => this.setState({ stateCode: e.target.value.toUpperCase() })} />
          <select style={input} value={this.state.status} onChange={(e) => this.setState({ status: e.target.value })}>
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <input style={input} placeholder="Search slug…" value={this.state.search}
                 onChange={(e) => this.setState({ search: e.target.value })} />
          <button style={btn(RED)} onClick={this.apply}>Apply</button>
        </div>

        {/* Bulk actions */}
        <div style={{ marginBottom: 12 }}>
          <button style={btn('#2E7D32')} disabled={this.state.busy} onClick={() => this.bulk('published')}>Publish selected</button>
          <button style={btn('#9E9E9E')} disabled={this.state.busy} onClick={() => this.bulk('draft')}>Unpublish selected</button>
          <span style={{ margin: '0 8px', color: '#bbb' }}>|</span>
          <button style={btn('#1565C0')} disabled={this.state.busy} onClick={() => this.applyToFilter('published')}>Publish ALL matching filter</button>
          <button style={btn('#777')} disabled={this.state.busy} onClick={() => this.applyToFilter('draft')}>Unpublish ALL matching filter</button>
          {this.state.message ? <span style={{ marginLeft: 12, color: RED, fontWeight: 600 }}>{this.state.message}</span> : null}
        </div>

        {/* Table */}
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#fafafa', textAlign: 'left' }}>
                <th style={{ padding: 10, width: 36 }}></th>
                <th style={{ padding: 10 }}>Service</th>
                <th style={{ padding: 10 }}>City</th>
                <th style={{ padding: 10 }}>State</th>
                <th style={{ padding: 10 }}>Path</th>
                <th style={{ padding: 10 }}>Status</th>
                <th style={{ padding: 10 }}>Source</th>
                <th style={{ padding: 10 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.loading && <tr><td colSpan={8} style={{ padding: 20 }}>Loading…</td></tr>}
              {!data.loading && data.error && <tr><td colSpan={8} style={{ padding: 20, color: '#c0392b' }}>Couldn’t load pages. Please retry — click Apply or reload.</td></tr>}
              {!data.loading && !data.error && rows.length === 0 && <tr><td colSpan={8} style={{ padding: 20 }}>No pages found.</td></tr>}
              {!data.loading && rows.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ padding: 10 }}>
                    <input type="checkbox" checked={!!this.state.selected[p.id]} onChange={() => this.toggle(p.id)} />
                  </td>
                  <td style={{ padding: 10 }}>{p.serviceName}</td>
                  <td style={{ padding: 10 }}>{p.cityName}</td>
                  <td style={{ padding: 10 }}>{p.stateCode}</td>
                  <td style={{ padding: 10 }}>
                    <a href={p.status === 'published' ? p.path : `${p.path}?preview=1`} target="_blank" rel="noreferrer" style={{ color: RED }}>{p.path}</a>
                  </td>
                  <td style={{ padding: 10 }}><span style={badge(p.status)}>{p.status}</span></td>
                  <td style={{ padding: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: p.isAuto ? '#1a73e8' : '#8a6d00', background: p.isAuto ? '#e8f0fe' : '#fff3cd', padding: '2px 8px', borderRadius: 10 }}>
                      {p.isAuto ? 'Auto' : 'Edited'}
                    </span>
                  </td>
                  <td style={{ padding: 10, whiteSpace: 'nowrap' }}>
                    {p.status === 'published'
                      ? <button style={btn('#9E9E9E')} disabled={this.state.busy} onClick={() => this.run({ ids: [p.id], status: 'draft' })}>Unpublish</button>
                      : <button style={btn('#2E7D32')} disabled={this.state.busy} onClick={() => this.run({ ids: [p.id], status: 'published' })}>Publish</button>}
                    {' '}
                    <a href={`/siteadmin/seo/edit/${p.id}`} style={{ color: RED, marginLeft: 8 }}>Edit</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button style={btn(RED)} disabled={this.state.currentPage <= 1} onClick={() => this.go(-1)}>Prev</button>
          <span>Page {this.state.currentPage} / {totalPages} ({list.count || 0} match)</span>
          <button style={btn(RED)} disabled={this.state.currentPage >= totalPages} onClick={() => this.go(1)}>Next</button>
        </div>

        {/* ===== Setup guide (for staff) — plain English so anyone can manage SEO pages ===== */}
        <div style={{ marginTop: 36, padding: 24, background: '#f8f9fa', border: '1px solid #e3e6ea', borderRadius: 12, lineHeight: 1.6, color: '#33373b', maxWidth: 900 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Setup guide (for staff)</h2>
          <p style={{ marginTop: 8 }}>
            These are <b>programmatic SEO landing pages</b> — one per <b>service × city</b> (e.g.
            "TV mounting in Austin, TX"). They help customers find BestHand on Google. They are
            <b> created automatically</b>; your job here is mainly to <b>review and choose which ones go live</b>.
          </p>

          <h3 style={{ fontSize: 18, fontWeight: 700, margin: '22px 0 8px' }}>The 3 counters at the top</h3>
          <ul style={{ margin: 0 }}>
            <li><b>Total</b> — every page that exists.</li>
            <li><b>Published</b> — live on the website + listed in the sitemap Google reads.</li>
            <li><b>Draft</b> — created but not live yet (ready to activate).</li>
          </ul>

          <h3 style={{ fontSize: 18, fontWeight: 700, margin: '22px 0 8px' }}>Status: Draft vs Published</h3>
          <ul style={{ margin: 0 }}>
            <li><b>Published</b> = visible to the public, on the service hub page, and in <code>/sitemap.xml</code> (so Google can index it).</li>
            <li><b>Draft</b> = hidden from the public and the sitemap. The <b>Path</b> link still opens a preview (it adds <code>?preview=1</code>) so you can check it before publishing.</li>
          </ul>

          <h3 style={{ fontSize: 18, fontWeight: 700, margin: '22px 0 8px' }}>How pages get created (automatic)</h3>
          <ul style={{ margin: 0 }}>
            <li>When an admin <b>adds or edits a service category</b>, its SEO pages are generated automatically.</li>
            <li>A <b>nightly job</b> also fills in any missing city pages for every active category.</li>
            <li>You normally <b>don’t create pages by hand</b> — you just review them here and publish/unpublish.</li>
          </ul>

          <h3 style={{ fontSize: 18, fontWeight: 700, margin: '22px 0 8px' }}>How to publish / unpublish</h3>
          <ol style={{ margin: 0 }}>
            <li><b>Filter</b> by Service, State (e.g. <code>PA</code>), Status, or search a slug, then click <b>Apply</b>.</li>
            <li><b>One page:</b> use the button in its <b>Action</b> column.</li>
            <li><b>A few pages:</b> tick their checkboxes, then <b>Publish selected</b> / <b>Unpublish selected</b>.</li>
            <li><b>All pages of a service/state:</b> set the Service and/or State filter, then <b>Publish ALL matching</b> / <b>Unpublish ALL matching</b> (it asks for confirmation).</li>
            <li>Click a row’s <b>Path</b> to open the live page (or preview) in a new tab and check it.</li>
          </ol>

          <div style={{ background: '#fff7e6', border: '1px solid #ffe0a3', borderRadius: 8, padding: '10px 14px', margin: '18px 0' }}>
            <b>Only publish where we actually operate.</b> Keep this in sync with <b>Service Regions</b>
            (Admin → Service Regions): publish pages for <b>Open</b> states (e.g. PA, NJ, NY) and keep the
            rest as Draft. Publishing a city we don’t serve sends Google traffic we can’t fulfill.
          </div>

          <h3 style={{ fontSize: 18, fontWeight: 700, margin: '22px 0 8px' }}>Good to know</h3>
          <ul style={{ margin: 0 }}>
            <li>A page’s on-page <b>price estimate</b> is real only if its service category has active pricing set up.</li>
            <li>Publishing a page doesn’t rank it instantly — Google takes time to crawl and index. Be patient.</li>
            <li>Don’t mass-publish thousands of thin pages for cities you don’t serve; quality &gt; quantity for SEO.</li>
          </ul>
        </div>
      </div>
    );
  }
}

export default compose(
  withQuery(getAllSeoLandingPages, {
    name: 'seoPages',
    options: { variables: { currentPage: 1, limit: PAGE_SIZE }, fetchPolicy: 'network-only', ssr: false },
  }),
  withQuery(getSeoServices, { name: 'services', options: { fetchPolicy: 'cache-first', ssr: false } }),
  withMutation(setSeoLandingPagesStatus, { name: 'setStatus' }),
)(ManageSeoPages);
